import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useMemo, type ReactNode } from 'react';

import { Tabs } from 'app/shared-components/structure';
import useEventTracker from 'app/hooks/useEventTracker';
import { SERVICES_EVENTS } from 'app/tracking';
import { useUserRoles, useSelectedPharmacy } from 'app/hooks';

// Canadian fork of layouts/GroupServicesLayout: no Group questionnaires tab.
// The org-level Categories and Group services tabs are support-only in Canada
// (HEA-2112); every other role sees the pharmacy-level tabs only (HEA-2086).
const pharmacyTabs = [
  {
    label: 'Listed services',
    value: 'listed-services',
    path: '/services',
  },
  {
    label: 'Consultation rooms',
    value: 'consultation-rooms',
    path: '/services/consultation-rooms',
  },
  {
    label: 'Block-out days',
    value: 'block-out-days',
    path: '/services/block-out-days',
  },
];

const supportTabs = [
  ...pharmacyTabs,
  {
    label: 'Categories',
    value: 'categories',
    path: '/services/service-templates/categories',
  },
  {
    label: 'Group services',
    value: 'group-services',
    path: '/services/service-templates',
  },
];

interface GroupServicesLayoutProps {
  children: ReactNode;
}

export default function GroupServicesLayout({ children }: GroupServicesLayoutProps): JSX.Element {
  const trackEvent = useEventTracker();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedPharmacy = useSelectedPharmacy();
  const { isSupport } = useUserRoles();

  const tabs = isSupport ? supportTabs : pharmacyTabs;

  const selectedTab = useMemo(() => {
    // Sort tabs by path length (longest first) to match most specific paths first
    const sortedTabs = [...tabs].sort((a, b) => b.path.length - a.path.length);
    return sortedTabs.find((tab) => location.pathname.includes(tab.path))?.value;
  }, [location.pathname, tabs]);

  function handleTabChange(_event: unknown, newValue: string): void {
    // Track section clicked using lookup map
    const sectionEvent = tabEvents[newValue];
    if (sectionEvent) trackEvent(sectionEvent.action, sectionEvent);

    const newPath = tabs.find((tab) => tab.value === newValue)?.path;
    if (!newPath) return;

    let newUrl = newPath;
    if (newPath.startsWith('/services/service-templates')) {
      const organisationId = searchParams.get('organisationId') || selectedPharmacy?.chain_id;
      if (organisationId) newUrl += `?organisationId=${organisationId}`;
    }

    navigate(newUrl, { replace: true });
  }

  return (
    <Box className="grow flex flex-col px-20 py-16 gap-16 bg-white overflow-hidden">
      <Box className="flex items-center">
        <Tabs
          tabs={tabs}
          selectedTab={selectedTab ?? ''}
          handleChange={handleTabChange}
          dividerAfterIndex={isSupport ? 2 : undefined}
        />
      </Box>
      {children}
    </Box>
  );
}

const tabEvents: Record<string, { action: string }> = {
  'listed-services': SERVICES_EVENTS.LISTED_SERVICES.SECTION_CLICKED,
  'consultation-rooms': SERVICES_EVENTS.CONSULTATION_ROOMS.SECTION_CLICKED,
  'block-out-days': SERVICES_EVENTS.BLOCK_OUT_DAYS.SECTION_CLICKED,
  categories: SERVICES_EVENTS.CATEGORIES.SECTION_CLICKED,
  'group-services': SERVICES_EVENTS.GROUP_SERVICES.SECTION_CLICKED,
};
